import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Award, Shield, CheckCircle, ArrowRight, Users, 
  FileText, Lock, Eye, EyeOff, AlertCircle
} from 'lucide-react';
import CertificationNav from '../components/CertificationNav';
import { useTranslation } from '../translations';

/**
 * CertificationOptIn - Consentimento LGPD Simplificado
 * 
 * Fluxo unificado: O trabalhador aceita o termo LGPD
 * para que seu selo profissional seja visível publicamente.
 * 
 * Tripé HeveaTalent:
 * - Justiça: Todos os vínculos são iguais
 * - Segurança: LGPD obrigatório para proteção de dados
 * - Simplicidade: Fluxo direto e claro
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

const CONTRACT_TYPES = [
  { value: 'clt', label: 'CLT - Carteira Assinada', icon: '\u{1F454}', description: 'Trabalhador com carteira assinada e salario fixo' },
  { value: 'clt_percentage', label: 'CLT + Porcentagem', icon: '\u{1F454}\u{1F4B0}', description: 'Carteira assinada + porcentagem da producao' },
  { value: 'partner', label: 'Parceiro / Meeiro', icon: '\u{1F91D}', description: 'Recebe porcentagem da producao, com autonomia' },
  { value: 'temporary', label: 'Temporario / Safra', icon: '\u{1F4C5}', description: 'Contrato por periodo determinado' }
];

const CertificationOptIn = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedContract, setSelectedContract] = useState('');
  const [lgpdTerm, setLgpdTerm] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [alreadyActive, setAlreadyActive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        
        const statusRes = await fetch(`${BACKEND_URL}/api/certification/status`, { headers });
        if (statusRes.ok) {
          const status = await statusRes.json();
          setCurrentStatus(status);
          if (status.certification_status === 'active') {
            setAlreadyActive(true);
          }
        }

        const termRes = await fetch(`${BACKEND_URL}/api/certification/lgpd-term`);
        if (termRes.ok) {
          const term = await termRes.json();
          setLgpdTerm(term);
        }
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async () => {
    if (!selectedContract) {
      setError('Selecione seu tipo de vinculo');
      return;
    }
    if (!acceptedTerms) {
      setError('Aceite o termo LGPD para continuar');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Opt-in
      const optInRes = await fetch(`${BACKEND_URL}/api/certification/opt-in`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ contract_type: selectedContract, accept_terms: true })
      });

      if (!optInRes.ok) {
        const data = await optInRes.json();
        setError(data.detail || 'Erro ao processar');
        return;
      }

      // Step 2: Consent
      const consentRes = await fetch(`${BACKEND_URL}/api/certification/consent`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          accept_consent: true,
          privacy_settings: { program_active: true, profile_visibility: 'public' }
        })
      });

      if (consentRes.ok) {
        navigate('/', { state: { message: 'Consentimento LGPD aceito com sucesso!' } });
      } else {
        const data = await consentRes.json();
        setError(data.detail || 'Erro ao aceitar consentimento');
      }
    } catch (err) {
      setError('Erro de conexao. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CertificationNav />
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-8">
        <div className="container mx-auto px-4 max-w-2xl">
        
          {initialLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando...</p>
              </div>
            </div>
          ) : alreadyActive ? (
            /* Já ativo */
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Consentimento LGPD Ativo</h2>
              <p className="text-gray-600 mb-6">
                Seus dados estao protegidos e seu selo profissional esta visivel publicamente.
              </p>
              {currentStatus?.opted_in_date && (
                <p className="text-green-600 text-sm mb-4">
                  Aceito em: {new Date(currentStatus.opted_in_date).toLocaleDateString('pt-BR')}
                </p>
              )}
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Voltar ao Inicio
              </button>
            </div>
          ) : (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800">Consentimento LGPD</h1>
              <p className="text-gray-600 mt-2">
                Para que seu selo profissional seja visivel publicamente, aceite o termo abaixo.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-lg p-6">
              {/* Tipo de Vínculo */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                  <h2 className="text-xl font-bold text-gray-800">Tipo de Vinculo</h2>
                </div>
                <div className="space-y-2">
                  {CONTRACT_TYPES.map(contract => (
                    <button
                      key={contract.value}
                      onClick={() => setSelectedContract(contract.value)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        selectedContract === contract.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                      data-testid={`contract-${contract.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{contract.icon}</span>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800 text-sm">{contract.label}</p>
                          <p className="text-xs text-gray-500">{contract.description}</p>
                        </div>
                        {selectedContract === contract.value && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Termo LGPD */}
              {lgpdTerm && (
                <div className="mb-6 border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-green-600" />
                    <h2 className="text-xl font-bold text-gray-800">Termo LGPD</h2>
                  </div>

                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-blue-600" />
                      Dados coletados:
                    </h3>
                    <ul className="space-y-1 ml-6">
                      {lgpdTerm.collected_data.map((item, i) => (
                        <li key={i} className="text-gray-600 text-xs">- {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                        <Eye className="w-4 h-4 text-green-600" />
                        Visivel publicamente:
                      </h3>
                      <ul className="space-y-1">
                        <li className="text-gray-700 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Nome e selo</li>
                        <li className="text-gray-700 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Experiencia</li>
                        <li className="text-gray-700 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Tendencia</li>
                      </ul>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                        <EyeOff className="w-4 h-4 text-red-600" />
                        Nunca compartilhado:
                      </h3>
                      <ul className="space-y-1">
                        <li className="text-gray-700 text-xs flex items-center gap-1"><Lock className="w-3 h-3 text-red-600" /> Historico detalhado</li>
                        <li className="text-gray-700 text-xs flex items-center gap-1"><Lock className="w-3 h-3 text-red-600" /> Notas do avaliador</li>
                        <li className="text-gray-700 text-xs flex items-center gap-1"><Lock className="w-3 h-3 text-red-600" /> Dados de contato</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                      <Lock className="w-4 h-4 text-purple-600" />
                      Seus direitos:
                    </h3>
                    <ul className="space-y-1 ml-6">
                      {lgpdTerm.worker_rights.map((item, i) => (
                        <li key={i} className="text-gray-600 text-xs flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Checkbox de aceite */}
              <label className="flex items-start gap-3 p-4 bg-green-50 rounded-lg cursor-pointer border-2 border-green-200 mb-4" data-testid="accept-lgpd-checkbox">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 accent-green-600"
                />
                <span className="text-gray-700 text-sm">
                  Li e aceito o termo LGPD. Autorizo o uso dos meus dados conforme descrito acima para fins de certificacao profissional.
                </span>
              </label>

              {/* Botão submit */}
              <button
                onClick={handleSubmit}
                disabled={!selectedContract || !acceptedTerms || loading}
                className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                  selectedContract && acceptedTerms && !loading
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-300 cursor-not-allowed text-gray-500'
                }`}
                data-testid="submit-lgpd-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    Aceitar e Ativar Selo Publico
                  </>
                )}
              </button>

              {/* Feedback do que falta */}
              {(!selectedContract || !acceptedTerms) && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Para ativar, voce precisa:
                  </p>
                  <ul className="mt-1 ml-6 space-y-1">
                    {!selectedContract && (
                      <li className="text-amber-700 text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0"></span>
                        Selecionar seu tipo de vinculo acima
                      </li>
                    )}
                    {!acceptedTerms && (
                      <li className="text-amber-700 text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0"></span>
                        Marcar o checkbox de aceite do termo LGPD
                      </li>
                    )}
                  </ul>
                </div>
              )}

              <p className="mt-4 text-xs text-gray-500 text-center">
                Voce pode revogar seu consentimento a qualquer momento.
              </p>
            </div>

            <div className="mt-8 text-center text-gray-500 text-sm">
              <p>Tripe HeveaTalent: Justica / Seguranca / Simplicidade</p>
            </div>
          </>
          )}
        </div>
      </div>
    </>
  );
};

export default CertificationOptIn;
